import React from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import "../../OurService/styles/RiskBasedOnLocation.css";

const SUPPORT_EMAIL = "info@myredfolder.com";

const ACCENT = "#b71c1c";
const MUTED = "#6b625b";
const RULE = "#e6dfd2";

const SITE = "MYREDFOLDER® / www.myredfolder.com";

const PILLS = [
  "Encryption",
  "Multi-factor authentication",
  "Blockchain integrity",
  "Audits & testing",
  "Incident response",
];

const SECTIONS = [
  {
    title: "Introduction",
    items: [
      {
        text: `Welcome to the MYREDFOLDER® (www.myredfolder.com) website ("we," "us," or "our"). Security is of paramount importance to us, and we are dedicated to ensuring the utmost protection of your personal information, digital assets, financial information, and all other data you entrust to us. This Security Policy outlines the comprehensive security measures we have implemented to safeguard your information.`,
      },
      {
        text: `By accessing or using the ${SITE} website, you acknowledge that you have read, understood, and agreed to the terms of this Security Policy. If you do not agree with our security practices, please do not use the ${SITE} website.`,
      },
    ],
  },
  {
    title: "Security Measures",
    grid: true,
    items: [
      {
        title: "Blockchain Technology",
        text: `The ${SITE} website leverages blockchain technology to secure the integrity and immutability of your digital assets. Transactions and data stored on the blockchain are cryptographically protected against tampering.`,
      },
      {
        title: "Secure Socket Layer (SSL)",
        text: `We use SSL certificates to encrypt data transmitted between your device and the ${SITE} website, protecting it from unauthorized access during transmission.`,
      },
      {
        title: "Multi-Factor Authentication (MFA)",
        text: `To enhance access security, the ${SITE} website utilizes MFA methods, requiring additional verification steps beyond a password, such as a one-time code sent to your registered email or phone.`,
      },
      {
        title: "Quantum Layer Security",
        text: "Our system is fortified with quantum-resistant encryption to safeguard against potential quantum-based attacks.",
      },
      {
        title: "Redundant Servers",
        text: "We maintain or lease multiple, geographically distributed servers to ensure redundancy, data backup, and continuous service availability.",
      },
      {
        title: "Firewall Protection",
        text: `The ${SITE} website is protected by firewalls that monitor and control incoming and outgoing network traffic, preventing unauthorized access and potential threats.`,
      },
      {
        title: "Vulnerability Assessments and Penetration Testing",
        text: "Regular vulnerability assessments and penetration testing are conducted to identify and address potential security weaknesses proactively.",
      },
      {
        title: "Data Encryption",
        text: "All sensitive data, including your personal information and financial details, is encrypted both at rest and in transit to prevent unauthorized access.",
      },
      {
        title: "Access Control",
        text: "Access to your account and sensitive information is strictly controlled based on role-based access permissions.",
      },
    ],
  },
  {
    title: "Compliance and Auditing",
    items: [
      {
        title: "Legal and Regulatory Compliance",
        text: `The ${SITE} website adheres to all applicable laws, regulations, and industry standards related to data protection and privacy.`,
      },
      {
        title: "Security Audits",
        text: "Periodic security audits are conducted to evaluate the effectiveness of our security measures and identify areas for improvement.",
      },
    ],
  },
  {
    title: "Incident Response",
    items: [
      {
        title: "Incident Reporting",
        text: "If a security incident occurs, the MYREDFOLDER® IT security team follows a detailed incident response plan to promptly detect, mitigate, and remediate the impact.",
      },
      {
        title: "User Notification",
        text: "In the event of a data breach or security incident that may compromise your personal information or digital assets, we will notify you promptly in accordance with applicable laws.",
      },
    ],
  },
  {
    title: "Employee Training",
    items: [
      {
        title: "Security Awareness",
        text: "All employees undergo regular security awareness training to stay updated on best practices, security protocols, and potential threats.",
      },
    ],
  },
  {
    title: "Third-Party Services",
    items: [
      {
        text: `The ${SITE} website may integrate with third-party services or websites. We perform due diligence to ensure their security practices align with our standards before integrating with them.`,
      },
    ],
  },
  {
    title: "Change Management",
    items: [
      {
        text: "Any changes to security measures will be carefully planned, documented, and tested before implementation to minimize any potential impact on service or security.",
      },
    ],
  },
  {
    title: "User Responsibilities",
    items: [
      {
        text: "We encourage you to maintain the security of your MYREDFOLDER® account, including your login credentials, and promptly report any suspicious activity.",
      },
    ],
  },
];

const pad = (n) => String(n).padStart(2, "0");

function Row({ index, section }) {
  return (
    <Box
      component="section"
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "260px 1fr" },
        columnGap: 6,
        rowGap: 2,
        py: { xs: 3, md: 5 },
        borderTop: `1px solid ${RULE}`,
      }}
    >
      <Box sx={{ position: { md: "sticky" }, top: { md: 90 }, alignSelf: "start" }}>
        <Typography
          sx={{
            color: ACCENT,
            fontWeight: 700,
            fontSize: { xs: 28, md: 44 },
            lineHeight: 1,
            letterSpacing: "-0.02em",
            mb: 1,
          }}
        >
          {pad(index + 1)}
        </Typography>
        <Typography component="h2" className="primary-font-bold11">
          {section.title}
        </Typography>
      </Box>

      <Box
        sx={
          section.grid
            ? {
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                gap: { xs: 2.5, lg: 4 },
              }
            : { display: "flex", flexDirection: "column", gap: 2.5 }
        }
      >
        {section.items.map((item, i) => (
          <Box key={item.title || item.text}>
            {item.title && (
              <Typography
                component="h3"
                className="primary-font-bold15"
                sx={{ mb: 0.5 }}
              >
                <Box component="span" sx={{ color: MUTED, mr: 1, fontWeight: 500 }}>
                  {index + 1}.{i + 1}
                </Box>
                {item.title}
              </Typography>
            )}
            <Typography
              className="primary-font-normal-2"
              sx={{ lineHeight: 1.75, maxWidth: 1024 }}
            >
              {item.text}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function Security() {
  const navigate = useNavigate();

  return (
    <Box>
      {/* Hero */}
      <section
        className="mrf-banner mrf-banner--plain"
        style={{ height: "auto", padding: "28px 0 40px" }}
      >
        <div className="mrf-banner__inner">
          <Box
            onClick={() => navigate(-1)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && navigate(-1)}
            sx={{
              // display: "inline-flex",
              display: "none",
              alignItems: "center",
              alignSelf: "flex-start",
              gap: 0.5,
              cursor: "pointer",
              color: "#fff",
              opacity: 0.85,
              "&:hover": { opacity: 1 },
              mb: 2.5,
            }}
          >
            <ArrowBackIcon fontSize="small" />
            <Typography className="primary-font-medium-2" sx={{ color: "inherit" }}>
              Go Back
            </Typography>
          </Box>

          <p className="mrf-banner__eyebrow">Security</p>
          <h1 className="mrf-banner__title" style={{ marginBottom: 24 }}>
            How we keep your information safe
          </h1>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {PILLS.map((p) => (
              <Box
                key={p}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "999px",
                  color: "#fff",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  border: "1px solid rgba(255,255,255,0.25)",
                  fontSize: 14,
                }}
              >
                {p}
              </Box>
            ))}
          </Box>
        </div>
      </section>

      {/* Sections */}
      <div className="ourservices-header-container">
        <Box sx={{ pb: { xs: 3, md: 5 } }}>
          {SECTIONS.map((section, i) => (
            <Row key={section.title} index={i} section={section} />
          ))}
        </Box>
      </div>

      {/* Contact band */}
      <section
        className="mrf-banner mrf-banner--plain"
        style={{ height: "auto", padding: "48px 0" }}
      >
        <div className="mrf-banner__inner">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.2fr 1fr" },
              gap: { xs: 3, md: 6 },
              alignItems: "center",
            }}
          >
            <Box>
              <p className="mrf-banner__eyebrow">Contact Us</p>
              <Typography
                component="h2"
                sx={{
                  fontFamily: '"Bebas Neue", "Poppins", sans-serif',
                  fontWeight: 700,
                  fontSize: "clamp(28px, 3.5vw, 44px)",
                  lineHeight: 1.05,
                  color: "#fff",
                  mb: 1.5,
                }}
              >
                Questions about our security?
              </Typography>
              <Typography
                className="primary-font-normal-2"
                sx={{ color: "rgba(255,255,255,0.8)", lineHeight: 1.7, maxWidth: 560 }}
              >
                If you have any questions, concerns, or feedback about this
                Security Policy or the {SITE} website security measures, please
                contact our support team.
              </Typography>
            </Box>

            <Box
              component="a"
              href={`mailto:${SUPPORT_EMAIL}`}
              sx={{
                display: "block",
                textDecoration: "none",
                p: { xs: 2, md: 3 },
                borderRadius: "12px",
                backgroundColor: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.18)",
                transition: "background-color 0.2s, border-color 0.2s",
                "&:hover, &:focus-visible": {
                  backgroundColor: "rgba(255,255,255,0.14)",
                  borderColor: "rgba(255,255,255,0.4)",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    backgroundColor: ACCENT,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <MailOutlineIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    className="primary-font-medium-2"
                    sx={{ color: "rgba(255,255,255,0.65)", fontSize: 13 }}
                  >
                    Email our support team
                  </Typography>
                  <Typography
                    className="primary-font-bold15"
                    sx={{ color: "#fff", wordBreak: "break-all" }}
                  >
                    {SUPPORT_EMAIL}
                  </Typography>
                </Box>
                <ArrowForwardIcon sx={{ color: "#fff", ml: "auto", flexShrink: 0 }} />
              </Box>
            </Box>
          </Box>

          <Typography
            className="primary-font-normal-2"
            sx={{
              color: "rgba(255,255,255,0.6)",
              lineHeight: 1.7,
              fontSize: 14,
              mt: { xs: 3, md: 5 },
              pt: 2.5,
              borderTop: "1px solid rgba(255,255,255,0.15)",
              maxWidth: 820,
            }}
          >
            Thank you for entrusting the MYREDFOLDER®, MW Concepts and
            Marketing, LLC, and the National Emergency Planning and Training
            Association, LLC with your personal and digital assets. We are
            committed to upholding the highest security standards to protect
            your information.
          </Typography>
        </div>
      </section>
    </Box>
  );
}
